import { useRef } from 'react';
import { isNil, omitBy } from 'lodash';

/**
 * Caches entity references to prevent unmounting during view transitions.
 * Only updates cache with non-nil values, preserving previous values when new props are undefined.
 *
 * @param entities - An object containing entity references to be cached
 * @returns The cached entities with preserved non-nil values from previous renders
 *
 * @example
 * const { project, workspace } = useCachedEntities({
 *   project: projectProp,
 *   workspace: workspaceProp
 * });
 */
export function useCachedEntities<T extends Record<string, any>>(entities: T) {
  const cachedEntities = useRef(entities);

  cachedEntities.current = {
    ...cachedEntities.current,
    ...omitBy(entities, isNil),
  };

  return cachedEntities.current;
}
